import { createSelector } from "reselect";
import get from "lodash/get";
import map from "lodash/map";
import head from "lodash/head";
import isEmpty from "lodash/isEmpty";
import uniq from "lodash/uniq";
import find from "lodash/find";
import filter from "lodash/filter";
import includes from "lodash/includes";
import moment from "moment";
import { urlParam } from "utils/helpers";

import {
  numberFormatSelector,
  localeSelector,
  paymentStatusDetailsSelector,
  dateOptionsSelector,
  monthOptionsSelector,
  yearOptionsSelector,
  // selectCountriesSelector,
  slotDetailsSelector,
  selfPickupStationDetailsSelector,
  shippingMethodDetailsSelector
} from "ducks/selectors";
import {
  constructOrderDetails,
  sortAndFilterOrders,
  failureReasonConstructor
} from "utils/helpers";
import { selectedDeliveryMethodSelector } from "./forms/deliveryInfo";
import {
  eligibilityActionsStatusSelector,
  orderTerminationStatusSelector
} from "./eligibilitySettings";
import { loggedInUserDetailsSelector } from "./userProfileDetails";
import isArray from "lodash/isArray";

const guestOrderAPI = state => get(state.api, "guestOrder", {});
const guestOrderDomain = state => get(state.api, "guestOrder.data", {});
const userOrdersDomain = state => get(state, "api.userOrders.data", []);
const currentOrder = state => get(state.api, "order.data", {});
const userOrdersErrorDomain = state => get(state, "api.userOrders.error", {});
const pendingOrdersDomain = state =>
  get(state, "api.externalPendingOrders.data", []);
// ***************************************************************
// ******************* guestOrder selectors **********************
// ***************************************************************

export const userOrdersSelector = createSelector(
  userOrdersDomain,
  sortAndFilterOrders
);

export const allUserOrdersSelector = createSelector(userOrdersDomain, orders =>
  isEmpty(orders) ? null : orders
);

export const userOrdersErrorsSelector = createSelector(
  userOrdersErrorDomain,
  error => error
);
export const isUserOrdersFailed = createSelector(
  userOrdersErrorsSelector,
  error => !!error
);

export const userOrderRefsSelector = createSelector(
  userOrdersSelector,
  userOrders => map(userOrders, "order_ref")
);

export const pendingOrdersSelector = createSelector(
  pendingOrdersDomain,
  externalPendingOrders => {
    return externalPendingOrders;
  }
);

export const userOrderDetailsSelector = createSelector(
  userOrdersDomain,
  userOrderDetails => {
    let orderList = !isEmpty(userOrderDetails) ? userOrderDetails : [];
    // eslint-disable-next-line array-callback-return
    return orderList
      .filter(orderDetail => orderDetail.order_type !== "Service Order")
      .map(orderDetail => {
        const { id, state, order_ref, plan } = orderDetail;
        return {
          id: id,
          status: state,
          plan: get(plan, "name"),
          order_ref
        };
      });
  }
);

export const guestOrderSelector = createSelector(
  guestOrderDomain,
  userOrdersSelector,
  eligibilityActionsStatusSelector,
  orderTerminationStatusSelector,
  localeSelector,
  loggedInUserDetailsSelector,
  (
    guestOrder,
    userOrders,
    eligibilityActionsStatus,
    orderTerminationStatus,
    locale,
    loggedInUserDetails
  ) => {
    const current_order =
      find(
        userOrders,
        userOrder => userOrder?.order_ref === urlParam("order_ref")
      ) || head(userOrders);
    let order = isEmpty(guestOrder) ? current_order : guestOrder;

    if (!isEmpty(eligibilityActionsStatus) && !isEmpty(order)) {
      // const newSteps = compact([
      //   contructTerminationStatusStep(
      //     eligibilityActionsStatus,
      //     locale,
      //     orderTerminationStatus,
      //     order
      //   ),
      //   contructPortoutStatusStep(eligibilityActionsStatus, locale)
      // ]);
      const newSteps = [];
      const allSteps = [...get(order, "steps", []), ...newSteps].map(step => {
        // Temporary solution for appending "Reason:" to Order Cancellation description
        if (step.title === "Order Cancelled") {
          const description = get(step, "description", "");
          return {
            ...step,
            ...(description && { description: "Reason: " + description })
          };
        }
        return step;
      });
      order = { ...order, steps: uniq(allSteps) };
    }

    return order;
  }
);

export const isFetchingUserOrders = createSelector(guestOrderAPI, guestOrder =>
  get(guestOrder, "loading", false)
);

export const userOrdersDeliveryDetailsSelector = createSelector(
  guestOrderSelector,
  guestOrder => get(guestOrder, "order_deliver_detail")
);

export const isReInitiateOrReSubmitPortInSelector = createSelector(
  guestOrderSelector,
  guestOrder =>
    get(guestOrder, "next_step.action") === "reinitiate_number_transfer" ||
    get(guestOrder, "next_step.action") === "resubmit_mnp_number"
);

/**
 * Returns true
 *  - if userOrders API is called and it returned an empty array.
 * All other cases false
 */
export const isOrderDetailsEmptySelector = createSelector(
  state => get(state.api, "userOrders.data"),
  userOrders => {
    if (isArray(userOrders) && isEmpty(userOrders)) return true;
    return false;
  }
);

// ***************************************************************
// ****************** guestOrder makeSelect **********************
// ***************************************************************

export const makeSelectGuestOrder = () =>
  createSelector(
    guestOrderSelector,
    paymentStatusDetailsSelector,
    numberFormatSelector,
    localeSelector,
    slotDetailsSelector,
    selfPickupStationDetailsSelector,
    shippingMethodDetailsSelector,
    constructOrderDetails
  );

export const currentOrderSelector = createSelector(
  currentOrder,
  userOrdersDomain,
  (currentOrder, orderList) => {
    const order = find(orderList, {
      order_ref: get(currentOrder, "orderReference") || urlParam("order_ref")
    });
    return order;
  }
);

export const makeSelectCurrentOrder = () =>
  createSelector(
    currentOrderSelector,
    paymentStatusDetailsSelector,
    numberFormatSelector,
    localeSelector,
    slotDetailsSelector,
    selfPickupStationDetailsSelector,
    shippingMethodDetailsSelector,
    constructOrderDetails
  );

export const makeSelectRowCurrentOrder = () =>
  createSelector(currentOrder, order => order);

export const makeSelectGuestOrderOrderRef = () =>
  createSelector(guestOrderSelector, guestOrder =>
    get(guestOrder, "order_ref")
  );

export const makeSelectGuestOrderEsimProfile = () =>
  createSelector(guestOrderSelector, guestOrder =>
    get(guestOrder, "esim_profile")
  );

export const userOrdersDetailsSelector = createSelector(
  userOrdersDeliveryDetailsSelector,
  selectedDeliveryMethodSelector,
  (deliveryDetails, deliveryMethod) => {
    if (isEmpty(deliveryDetails)) return {};

    const {
      zip_code,
      prefecture,
      city,
      address_line_1,
      address_line_2,
      contact_number
    } = deliveryDetails;

    return {
      delivery_zip_code: zip_code,
      delivery_state: prefecture,
      delivery_city: city,
      delivery_street_name: address_line_1,
      delivery_hse_blk_tower: address_line_2,
      contact_number: contact_number,
      delivery_method: deliveryMethod
    };
  }
);

export const inCompleteOrderSelector = createSelector(
  userOrdersDomain,
  dateOptionsSelector,
  monthOptionsSelector,
  yearOptionsSelector,
  // selectCountriesSelector,
  (orders, dateOptions, monthOptions, yearOptions, topCountries = {}) => {
    const order = filter(orders, order => includes(["IC"], order.state)).map(
      order => {
        const order_user_detail = get(order, "order_user_detail") || {};
        const order_deliver_detail = get(order, "order_deliver_detail") || {};
        const order_billing_detail = get(order, "order_billing_detail") || {};
        const {
          first_name,
          last_name,
          middle_name,
          phone_number,
          email,
          dob
        } = order_user_detail;
        const {
          zip_code,
          hse_blk_tower,
          city,
          unit_no,
          street_building_name,
          state
        } = order_deliver_detail;
        const dobMoment = dob ? moment(dob) : undefined;
        const dobData = dobMoment ? dobMoment.format("DD-MM-YYYY") : undefined;
        const year = dobData && find(yearOptions, { value: dobMoment.year() });
        const month =
          dobData && find(monthOptions, { value: dobMoment.format("MM") });
        const date =
          dob && find(dateOptions, { value: dobMoment.format("DD") });
        const billingcountryName = find(topCountries, {
          label: get(order_billing_detail, "country")
        });
        const deliverycountryName = find(topCountries, {
          label: get(order_deliver_detail, "country")
        });

        return {
          user_details: {
            first_name,
            last_name,
            middle_name,
            phone_number,
            email
          },
          order: {
            dob: {
              date: get(date, "value"),
              month: get(month, "value"),
              year: get(year, "value", "").toString()
            },
            delivery_address: {
              zip_code: zip_code,
              hse_blk_tower,
              unit_no,
              street_building_name,
              city,
              countr: deliverycountryName,
              state
            },
            billing_address: {
              zip_code: get(order_billing_detail, "zip_code"),
              hse_blk_tower: get(order_billing_detail, "hse_blk_tower"),
              unit_no: get(order_billing_detail, "unit_no"),
              street_building_name: get(
                order_billing_detail,
                "street_building_name"
              ),
              city: get(order_billing_detail, "city"),
              country: billingcountryName,
              state: get(order_billing_detail, "state")
            }
          },
          state: get(order, "state"),
          sim_type: get(order, "sim_type"),
          number_type: get(order, "contract_type"),
          selected_addons: get(order, "order_items"),
          telco: get(order, "port_donor"),
          port_number: get(order, "port_telco_account_number"),
          is_port_in_request: get(order, "is_port_in_request"),
          plan: get(order, "plan"),
          order_ref: get(order, "order_ref"),
          can_fetch_delivery_slots: true
        };
      }
    );
    return head(order);
  }
);

export const userServiceOrderDetailsSelector = createSelector(
  userOrdersDomain,
  localeSelector,
  (userOrdersDomain, locale) => {
    let orderList = !isEmpty(userOrdersDomain) ? userOrdersDomain : [];
    // eslint-disable-next-line array-callback-return
    return orderList
      .filter(orderData => orderData.order_type === "Service Order")
      .map(orderDetail => {
        const { id, state, order_ref, order_type } = orderDetail;
        return {
          id: id,
          status: state,
          plan:
            order_type === "Service Order"
              ? get(locale, "sim_replacement", "SIM replacement")
              : "",
          order_ref
        };
      });
  }
);

/**
 * Failing KYC can be due to multiple reasons "NameMismatch, DocumentTypeMismatch, Manipulation", etc.
 * Reasons for failing KYC are returned from backend
 * Value from backend is a string. And if there are multiple reasons, they will be separated by commas.
 * Example 1: "NameMismatch" (single reason)
 * Example 2: "NameMismatch,AddressMismatch,DOBMismatch" (multiple reason)
 * Example 3: null (no reason)
 * In the scenario of "Others" (i.e., custom reason) Reason will not be a localization value.
 * Example 4: "This is a custom reason for rejecting the Identity Document" ("Others" reason)
 * @return {string} formatted message for displaying user's reason for failing KYC
 */
export const kycFailureReason = createSelector(
  guestOrderSelector,
  localeSelector,
  (order, locale) => {
    let kycFailureReasons = get(order, "substates.APPROVAL.reason", "") ?? "";
    return failureReasonConstructor(
      kycFailureReasons,
      "kyc_failure_end_message",
      locale
    );
  }
);

export const makeSelectUserOrders = () => userOrdersSelector;
export const makeSelectUserOrderRefs = () => userOrderRefsSelector;
export const makeSelectIncompleteOrder = () => inCompleteOrderSelector;
export const makeSelectIsUserOrdersFailed = () => isUserOrdersFailed;
export const makeSelectIsFetchingUserOrders = () => isFetchingUserOrders;
export const makeSelectUserOrderDetailsSelector = () =>
  userOrdersDetailsSelector;
export const makeSelectIsReInitiatePortIn = () =>
  isReInitiateOrReSubmitPortInSelector;
export const makeSelectIsOrderDetailsEmpty = () => isOrderDetailsEmptySelector;
export const makeSelectOrderDetailsData = () => userOrderDetailsSelector;
export const makeSelectServiceOrderDetailsData = () =>
  userServiceOrderDetailsSelector;
export const makeSelectkycFailureReason = () => kycFailureReason;
export const makeSelectPendingOrders = () => pendingOrdersSelector;
export const makeSelectAllUserOrders = () => allUserOrdersSelector;
